import { useCallback, useMemo, useState } from 'react';
import { FilterField } from '@erp/ui';
import { SearchableSelect } from '@/components/ui/searchable-select';

const MAX_FILTER_OPTIONS = 250;

export interface ColumnTextFilter<TData> {
  key: string;
  label: string;
  getValue: (row: TData) => unknown;
  disabled?: boolean;
  className?: string;
}

export function useColumnTextFilters<TData>(
  rows: TData[],
  columns: ColumnTextFilter<TData>[],
) {
  const [filters, setFilters] = useState<Record<string, string>>({});

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, value]) => value.trim() !== ''),
    [filters],
  );

  const filteredRows = useMemo(() => {
    if (activeFilters.length === 0) return rows;
    const filterable = new Map(columns.filter((column) => !column.disabled).map((column) => [column.key, column]));
    return rows.filter((row) =>
      activeFilters.every(([key, value]) => {
        const column = filterable.get(key);
        if (!column) return true;
        return normalizeFilterValue(column.getValue(row)).trim() === normalizeFilterValue(value).trim();
      }),
    );
  }, [activeFilters, columns, rows]);

  const setFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value.trim() === '') delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const clearColumnFilters = useCallback(() => setFilters({}), []);

  return {
    filters,
    setFilter,
    filteredRows,
    hasColumnFilters: activeFilters.length > 0,
    clearColumnFilters,
  };
}

interface ColumnTextFilterFieldsProps<TData> {
  rows: TData[];
  columns: ColumnTextFilter<TData>[];
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  allLabel?: string;
  searchPlaceholder?: string;
  minWidth?: string | number;
}

export function ColumnTextFilterFields<TData>({
  rows,
  columns,
  filters,
  onFilterChange,
  allLabel = 'Tous',
  searchPlaceholder = 'Rechercher',
  minWidth = 160,
}: ColumnTextFilterFieldsProps<TData>) {
  return (
    <>
      {columns.filter((column) => !column.disabled).map((column) => {
        const options = getColumnFilterOptions(rows, column);
        const selectedValue = filters[column.key]?.trim() ?? '';
        if (!selectedValue && options.length < 2) return null;
        const renderedOptions = selectedValue && !options.some((option) => option.value === selectedValue)
          ? [{ value: selectedValue, label: selectedValue }, ...options]
          : options;

        return (
          <FilterField key={`${column.key}-filter`} label={column.label} minWidth={minWidth}>
            <SearchableSelect
              options={renderedOptions}
              value={selectedValue}
              onChange={(value) => onFilterChange(column.key, value)}
              label={column.label}
              placeholder={allLabel}
              searchPlaceholder={searchPlaceholder}
              className={`h-8 ${column.className ?? ''}`}
            />
          </FilterField>
        );
      })}
    </>
  );
}

function getColumnFilterOptions<TData>(rows: TData[], column: ColumnTextFilter<TData>) {
  const options = new Map<string, string>();

  for (const row of rows) {
    const rawValue = column.getValue(row);
    const value = normalizeFilterValue(rawValue).trim();
    const label = formatFilterOptionLabel(rawValue).trim();
    if (!value || !label) continue;
    if (!options.has(value)) options.set(value, label);
    if (options.size >= MAX_FILTER_OPTIONS) break;
  }

  return Array.from(options, ([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
}

function formatFilterOptionLabel(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(formatFilterOptionLabel).filter(Boolean).join(' ');
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map(formatFilterOptionLabel).filter(Boolean).join(' ');
  }
  return String(value);
}

export function normalizeFilterValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(normalizeFilterValue).join(' ');
  if (value instanceof Date) return value.toISOString().toLowerCase();
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map(normalizeFilterValue).join(' ');
  }
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
